import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";

const SERIES = [
  { key: "finishing", label: "Finishing",    stroke: "#3B82F6" },
  { key: "defending", label: "Defending",    stroke: "#EF4444" },
  { key: "passing",   label: "Passing Game", stroke: "#EAB308" },
  { key: "overall",   label: "Overall",      stroke: "#10B981" },
];

/** "May '19" — compact for a multi-year x-axis. */
function tickDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const m = d.toLocaleDateString("en-GB", { month: "short" });
    const y = String(d.getFullYear()).slice(2);
    return `${m} '${y}`;
  } catch { return iso; }
}

function longDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div
      data-testid="career-tooltip"
      className="rounded-md border border-border bg-popover/95 backdrop-blur px-3.5 py-3 shadow-lg text-xs min-w-[220px] max-w-[300px]"
    >
      <div className="font-display uppercase tracking-[0.18em] text-[10px] text-muted-foreground mb-1.5" data-testid="career-tooltip-season">
        Season {row.season_short || row.season}
      </div>
      {row.season_league_name && (
        <div className="text-[11px] text-foreground/85 mb-2 truncate" data-testid="career-tooltip-league">
          {row.season_league_name}
        </div>
      )}
      <div className="space-y-1 border-t border-border pt-1.5">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ background: s.stroke }} />
              <span className="text-foreground">{s.label}</span>
            </span>
            <span className="font-mono text-foreground/80">{row[s.key] != null ? `${Number(row[s.key]).toFixed(1)}%` : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomLegend() {
  return (
    <ul
      data-testid="career-legend"
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-2 pb-1 pt-2"
    >
      {SERIES.map((s) => (
        <li key={s.key} className="flex items-center gap-2 text-xs font-display uppercase tracking-[0.14em]">
          <span className="inline-block h-2.5 w-3.5 rounded-[2px]" style={{ background: s.stroke }} />
          <span className="text-foreground/80">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

export default function CareerChart({ playerId, playerName }) {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get(`/players/${playerId}/career`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load career timeline"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId, ignoreFriendlies]);

  const chartData = useMemo(() => data?.points || [], [data]);
  const pointCount = chartData.length;
  const tooShort = pointCount < 2;

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="career-chart">
      <header className="px-6 pt-6 pb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Per-season form · isolation</div>
          <h2 className="font-display uppercase tracking-tight text-xl" data-testid="career-card-title">
            {playerName ? `${playerName}'s Career Timeline` : "Career Timeline"}
          </h2>
        </div>
        {/* Phase 11.11 — season-count badge removed per design; title stands alone. */}
      </header>

      <div className="px-2 pt-2 pb-4">
        <div style={{ width: "100%", height: 360, minHeight: 360 }} data-testid="career-chart-canvas">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="career-loading">
              Loading career…
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="career-error">
              {error}
            </div>
          ) : tooShort ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground gap-1" data-testid="career-empty">
              <div className="font-display uppercase tracking-[0.18em] text-xs">Not enough season data</div>
              <div className="text-[11px]">
                {pointCount === 0
                  ? "No matches found for this player yet."
                  : "Only one season on record — career timeline needs at least two."}
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={320}>
              <LineChart data={chartData} margin={{ top: 18, right: 28, left: 8, bottom: 26 }}>
                <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                <XAxis
                  dataKey="match_date"
                  tickFormatter={tickDate}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                  tickLine={false}
                  axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                  minTickGap={28}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                  tickLine={false}
                  axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                  width={32}
                  tickFormatter={(v) => `${v}`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "currentColor", strokeOpacity: 0.18, strokeDasharray: "3 3" }} />
                <Legend content={<CustomLegend />} verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.stroke}
                    strokeWidth={2}
                    dot={{ r: 3.5, strokeWidth: 0, fill: s.stroke }}
                    activeDot={{ r: 5.5, strokeWidth: 2, stroke: "var(--background, #fff)", fill: s.stroke }}
                    isAnimationActive
                    animationDuration={500}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </section>
  );
}
